import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MessageListQuery,
  Scout,
  ScoutConnection,
  ScoutSortEnum,
  OrganizationUserSelectQuery,
  SendScoutMessageInput,
} from '@/types/enterprise';
import { api } from '@/lib/api/client';
import { useMe } from '@/features/v2/account/repository/me-store';

export const useMessage = () => {
  const onSend = async (scoutId: string, input: Omit<SendScoutMessageInput, 'scoutId'>) => {
    await api.post(`/api/v1/enterprise/scouts/${scoutId}/messages`, input);
  };

  const onRead = async (scoutId: string) => {
    await api.post(`/api/v1/enterprise/scouts/${scoutId}/read`, {});
  };

  return { onSend, onRead };
};

type ScoutListFilter = {
  projectId?: string | null;
  sort?: ScoutSortEnum | null;
  unReplyOnly?: boolean | null;
  unReadOnly?: boolean | null;
  message?: string | null;
};

type ScoutListParams = {
  filter?: ScoutListFilter;
  limit: number;
  offset: number;
};

export function useScoutListQuery(params: ScoutListParams) {
  const filterKey = JSON.stringify(params.filter ?? {});
  const filter = useMemo(() => JSON.parse(filterKey) as ScoutListFilter, [filterKey]);
  const restPath = useMemo(() => buildScoutListPath({ filter, limit: params.limit, offset: params.offset }), [filter, params.limit, params.offset]);
  const [state, setState] = useState<{ data?: MessageListQuery; fetching: boolean; error?: Error }>({ fetching: true });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    api
      .get(restPath)
      .then((scouts: ScoutConnection) => {
        if (!canceled) setState({ data: { scouts }, fetching: false });
      })
      .catch((error) => {
        if (!canceled) setState({ fetching: false, error: error instanceof Error ? error : new Error('メッセージ一覧の取得に失敗しました') });
      });
    return () => {
      canceled = true;
    };
  }, [reloadKey, restPath]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return [state, refetch] as const;
}

export function useScoutMessageQuery(id: string | undefined) {
  const me = useMe();
  const [state, setState] = useState<{ data?: { scout: Scout }; fetching: boolean; error?: Error }>({ fetching: !!id });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    api.get(`/api/v1/enterprise/scouts/${id}`)
      .then((scout) => {
        if (!canceled) setState({ data: { scout }, fetching: false });
      })
      .catch((error) => {
        if (!canceled) setState({ fetching: false, error: error instanceof Error ? error : new Error('メッセージ詳細の取得に失敗しました') });
      });
    return () => {
      canceled = true;
    };
  }, [id, reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  const composedState = useMemo(() => ({
    data: state.data && me.data
      ? { me: me.data, scout: state.data.scout }
      : undefined,
    fetching: state.fetching || me.fetching,
    error: state.error || me.error,
  }), [state, me.data, me.fetching, me.error]);

  return [composedState, refetch] as const;
}

export function useOrganizationUsersQuery() {
  const [state, setState] = useState<{ data?: OrganizationUserSelectQuery; fetching: boolean; error?: Error }>({ fetching: true });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    api
      .get('/api/v1/enterprise/users')
      .then((organizationUsers: OrganizationUserSelectQuery['organizationUsers']) => {
        if (!canceled) setState({ data: { organizationUsers }, fetching: false });
      })
      .catch((error) => {
        if (!canceled) setState({ fetching: false, error: error instanceof Error ? error : new Error('ユーザー一覧の取得に失敗しました') });
      });
    return () => {
      canceled = true;
    };
  }, [reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return [state, refetch] as const;
}

function buildScoutListPath({ filter, limit, offset }: ScoutListParams) {
  const search = new URLSearchParams();
  search.set('limit', String(limit));
  search.set('offset', String(offset));
  if (filter?.projectId) search.set('projectId', String(filter.projectId));
  if (filter?.sort) search.set('sort', String(filter.sort));
  if (filter?.unReplyOnly != null) search.set('unReplyOnly', String(filter.unReplyOnly));
  if (filter?.unReadOnly != null) search.set('unReadOnly', String(filter.unReadOnly));
  if (filter?.message) search.set('message', String(filter.message));
  return `/api/v1/enterprise/scouts?${search.toString()}`;
}
